/*  ========================================================================
    # Components - Scroll Slider
    ========================================================================  */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { motion, MotionConfig, useReducedMotion } from 'motion/react';
import useLocalization from '../hooks/useLocalization';
import useTranslation from '../hooks/useTranslation';
import useResize from '../hooks/useResize';

const ScrollSlider = ({ scrollAreaRef, scrollBy }) => {
  const locale = useLocalization();
  const __ = useTranslation();
  const reduced = useReducedMotion();
  const [scrollX, setScrollX] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [visibleWidth, setVisibleWidth] = useState(0);

  const onScroll = useCallback(() => {
    setScrollX(scrollAreaRef.current.scrollLeft || 0);
  }, [scrollAreaRef, setScrollX]);

  const onResize = useCallback(() => {
    setVisibleWidth(scrollAreaRef.current.clientWidth || 0);
    setScrollWidth(scrollAreaRef.current.scrollWidth || 0);
    onScroll(); // Also update scroll values
  }, [scrollAreaRef, setVisibleWidth, setScrollWidth, onScroll]);

  // Register resize event listeners to update dimensions
  useResize(scrollAreaRef, onResize);

  // Register scroll event listeners
  useEffect(() => {
    if (!scrollAreaRef.current) return;
    scrollAreaRef.current.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollAreaRef.current?.removeEventListener('scroll', onScroll);
  }, [scrollAreaRef, onScroll]);

  // Handle scroll button click
  const handleScroll = useCallback(
    direction => {
      if (!scrollAreaRef.current) return;
      scrollAreaRef.current.scrollBy({
        left: direction * scrollBy,
        behavior: reduced ? 'instant' : 'smooth'
      });
    },
    [scrollAreaRef, scrollBy, reduced]
  );

  const sliderRequired = useMemo(() => visibleWidth < scrollWidth, [visibleWidth, scrollWidth]);
  return (
    <MotionConfig reducedMotion='user'>
      <motion.div
        className='c-universal-slider'
        initial={false}
        animate={{
          marginTop: sliderRequired ? 'var(--slider-gap)' : 0,
          height: sliderRequired ? 'auto' : 0,
          opacity: sliderRequired ? 1 : 0
        }}
        aria-hidden={!sliderRequired}
      >
        <div className='c-universal-slider__indicator-wrapper' aria-hidden>
          <motion.div
            className='c-universal-slider__indicator'
            initial={false}
            animate={{
              left: `${(scrollX / scrollWidth || 0) * 100}%`,
              right: `${100 - ((scrollX + visibleWidth) / scrollWidth || 0) * 100}%`
            }}
            transition={
              reduced
                ? {
                  duration: 0
                }
                : {
                  visualDuration: 0.5
                }
            }
          />
        </div>
        <motion.button
          onClick={() => handleScroll(-1)}
          className='c-universal-slider__button'
          disabled={scrollX <= 0}
          aria-label={__('scroll-slider-button-back')}
          whileTap={{ scale: 0.9 }}
        >
          <span className='u-screenreader'>{__('scroll-slider-button-back')}</span>
          <svg viewBox='0 0 50 50' aria-hidden>
            <use href={`${locale('svgSprite')}#arrow-left-big`} />
          </svg>
        </motion.button>
        <motion.button
          onClick={() => handleScroll(1)}
          className='c-universal-slider__button'
          disabled={(scrollX + visibleWidth) / scrollWidth >= 1}
          aria-label={__('scroll-slider-button-next')}
          whileTap={{ scale: 0.9 }}
        >
          <span className='u-screenreader'>{__('scroll-slider-button-next')}</span>
          <svg viewBox='0 0 50 50' aria-hidden>
            <use href={`${locale('svgSprite')}#arrow-right-big`} />
          </svg>
        </motion.button>
      </motion.div>
    </MotionConfig>
  );
};

ScrollSlider.propTypes = {
  scrollAreaRef: PropTypes.oneOfType([
    // Either a function
    PropTypes.func,
    // Or the instance of a DOM native element (see the note about SSR)
    PropTypes.shape({ current: PropTypes.instanceOf(Element) })
  ]).isRequired,
  scrollBy: PropTypes.number.isRequired
};

export default ScrollSlider;
